import { Component, ReactNode, RefObject } from 'react';
import { debounce } from 'lodash';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { HTMLElementWithAnchor, Tooltip } from '@ardoq/popovers';
import { Text, WithIcon } from './atoms';
import { TOOLTIP_DELAY, TOOLTIP_SELECTOR } from './consts';
import { TooltipTemplate } from './types';

export const getTooltipContent = (target: HTMLElementWithAnchor): ReactNode => {
  const { tooltipText, tooltipTemplate } = target.dataset;
  if (tooltipTemplate === TooltipTemplate.WITH_OPEN_IN_NEW_ICON_SMALL) {
    return (
      <WithIcon>
        <Text>{tooltipText}</Text>
        <Icon
          iconName={IconName.OPEN_IN_NEW}
          iconSize={IconSize.SMALL}
          color={colors.iconOnInverted}
        />
      </WithIcon>
    );
  }
  return tooltipText;
};

export const toAnchor = (
  target: Element | null
): HTMLElementWithAnchor | null => {
  if (!target) {
    return null;
  }
  if (!(target instanceof HTMLElement || target instanceof SVGElement)) {
    return null;
  }
  if (!target.dataset.tooltipText) {
    return null;
  }
  return target;
};

type TooltipManagerState = {
  readonly target: HTMLElementWithAnchor | null;
  readonly isOpen: boolean;
  readonly isMouseDown: boolean;
};

export class TooltipManager extends Component<
  Record<string, never>,
  TooltipManagerState
> {
  state: TooltipManagerState = {
    target: null,
    isOpen: false,
    isMouseDown: false,
  };

  anchorRef: RefObject<HTMLElementWithAnchor | null> = { current: null };
  anchorKey = 0;

  onMouseDown = () => {
    this.showTooltipDebounced.cancel();
    this.anchorRef = { current: null };
    this.setState({ isMouseDown: true, isOpen: false, target: null });
  };

  onMouseUp = () => this.setState({ isMouseDown: false });

  onMouseOver = (event: MouseEvent) => {
    if (this.state.isMouseDown) {
      return;
    }
    if (!(event.target instanceof Element)) return;
    const match = event.target.closest(TOOLTIP_SELECTOR);
    if (match === this.state.target) {
      return;
    }
    this.showTooltipDebounced.cancel();
    const anchor = toAnchor(match);
    if (anchor) {
      this.anchorKey += 1;
      this.anchorRef = { current: anchor };
      this.setState({ isOpen: false, target: anchor });
      this.showTooltipDebounced();
    } else {
      this.hideTooltip();
    }
  };

  showTooltip = () => this.setState({ isOpen: true });

  // eslint-disable-next-line @typescript-eslint/member-ordering
  showTooltipDebounced = debounce(this.showTooltip, TOOLTIP_DELAY);

  hideTooltip = () => {
    this.showTooltipDebounced.cancel();
    this.anchorRef = { current: null };
    this.setState({ isOpen: false, target: null });
  };

  componentDidMount() {
    document.addEventListener('mouseover', this.onMouseOver, true);
    document.addEventListener('mousedown', this.onMouseDown, true);
    document.addEventListener('mouseup', this.onMouseUp, true);
    document.addEventListener('scroll', this.hideTooltip, true);
  }

  componentWillUnmount() {
    document.removeEventListener('mouseover', this.onMouseOver, true);
    document.removeEventListener('mousedown', this.onMouseDown, true);
    document.removeEventListener('mouseup', this.onMouseUp, true);
    document.removeEventListener('scroll', this.hideTooltip, true);
  }

  render() {
    const { target, isOpen } = this.state;
    if (!target || !isOpen) {
      return null;
    }
    return (
      <Tooltip
        key={this.anchorKey}
        isOpen
        onClose={this.hideTooltip}
        anchorRef={this.anchorRef}
      >
        {getTooltipContent(target)}
      </Tooltip>
    );
  }
}
